/**
 * Envelope encryption using AES-256-GCM via Web Crypto API.
 *
 * Format: "enc:v1:<iv1_b64>:<ciphertext_b64>:<iv2_b64>:<wrappedDek_b64>"
 *
 * Each secret gets its own DEK (Data Encryption Key), wrapped by the master key.
 * Key rotation only re-wraps DEKs — no need to re-encrypt every secret.
 */

const ENVELOPE_PREFIX = 'enc:v1:';

function base64Encode(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function base64Decode(str: string): Uint8Array {
  const padded = str.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function importKey(rawKeyBase64: string): Promise<CryptoKey> {
  const keyBytes = base64Decode(rawKeyBase64);
  return crypto.subtle.importKey(
    'raw',
    keyBytes as BufferSource,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt'],
  );
}

async function aesEncrypt(key: CryptoKey, iv: Uint8Array, plaintext: Uint8Array): Promise<ArrayBuffer> {
  return crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, plaintext as BufferSource);
}

async function aesDecrypt(key: CryptoKey, iv: Uint8Array, ciphertext: Uint8Array): Promise<ArrayBuffer> {
  return crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, ciphertext as BufferSource);
}

/**
 * Generate a cryptographically secure secret (32 bytes, base64url encoded).
 * Use this instead of uuidv7() or crypto.randomUUID() for secrets.
 */
export function generateSecret(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64Encode(bytes.buffer);
}

/**
 * Generate a master key for envelope encryption (32 bytes, base64 encoded).
 * Run once to create the MASTER_KEY value for wrangler secrets.
 */
export function generateMasterKey(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes));
}

/**
 * Encrypt a plaintext string using envelope encryption (AES-256-GCM).
 *
 * 1. Generate a random DEK (Data Encryption Key)
 * 2. Encrypt plaintext with DEK
 * 3. Wrap (encrypt) the DEK with the master key
 * 4. Return all components as a prefixed string
 */
export async function encrypt(masterKeyBase64: string, plaintext: string): Promise<string> {
  const masterKey = await importKey(masterKeyBase64);

  // Generate DEK
  const dek = new Uint8Array(32);
  crypto.getRandomValues(dek);
  const dekKey = await crypto.subtle.importKey('raw', dek, { name: 'AES-GCM' }, true, ['encrypt']);

  // Encrypt plaintext with DEK
  const iv1 = new Uint8Array(12);
  crypto.getRandomValues(iv1);
  const ciphertext = await aesEncrypt(dekKey, iv1, new TextEncoder().encode(plaintext));

  // Wrap DEK with master key
  const iv2 = new Uint8Array(12);
  crypto.getRandomValues(iv2);
  const wrappedDek = await aesEncrypt(masterKey, iv2, dek);

  return `${ENVELOPE_PREFIX}${base64Encode(iv1.buffer)}:${base64Encode(ciphertext)}:${base64Encode(iv2.buffer)}:${base64Encode(wrappedDek)}`;
}

/**
 * Decrypt an envelope-encrypted string.
 *
 * 1. Unwrap the DEK using the master key
 * 2. Decrypt the ciphertext using the DEK
 */
export async function decrypt(masterKeyBase64: string, encrypted: string): Promise<string> {
  if (!encrypted.startsWith(ENVELOPE_PREFIX)) {
    throw new Error('Invalid encrypted format: missing enc:v1: prefix');
  }

  const parts = encrypted.slice(ENVELOPE_PREFIX.length).split(':');
  if (parts.length !== 4) {
    throw new Error('Invalid encrypted format: expected 4 components');
  }

  const [iv1B64, ciphertextB64, iv2B64, wrappedDekB64] = parts as [string, string, string, string];
  const iv1 = base64Decode(iv1B64);
  const ciphertext = base64Decode(ciphertextB64);
  const iv2 = base64Decode(iv2B64);
  const wrappedDek = base64Decode(wrappedDekB64);

  const masterKey = await importKey(masterKeyBase64);

  // Unwrap DEK
  const dekBytes = await aesDecrypt(masterKey, iv2, wrappedDek);
  const dekKey = await crypto.subtle.importKey('raw', dekBytes, { name: 'AES-GCM' }, false, ['decrypt']);

  // Decrypt ciphertext
  const plainBytes = await aesDecrypt(dekKey, iv1, ciphertext);
  return new TextDecoder().decode(plainBytes);
}

/**
 * Check whether a value is already envelope-encrypted.
 */
export function isEncrypted(value: string): boolean {
  return value.startsWith(ENVELOPE_PREFIX);
}

/**
 * Constant-time string comparison to prevent timing attacks.
 *
 * On length mismatch, compares `a` against itself to maintain constant time
 * (avoids leaking length information through early return).
 */
export function timingSafeEqual(a: string, b: string): boolean {
  const lengthMatch = a.length === b.length;
  if (!lengthMatch) {
    b = a;
  }

  let result = lengthMatch ? 0 : 1;
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return result === 0;
}

export function timingSafeEqualBytes(a: Uint8Array, b: Uint8Array): boolean {
  const lengthMatch = a.length === b.length;
  if (!lengthMatch) {
    b = a; // avoid early-return timing oracle on length mismatch
  }

  let result = lengthMatch ? 0 : 1;
  for (let i = 0; i < a.length; i++) {
    result |= a[i] ^ b[i];
  }
  return result === 0;
}
